/*
 * Content lifecycle: who owns an item, when it was last reviewed, when it is due again.
 *
 *   owner                 { name, email }   gets the review reminder
 *   coOwners              [{ name, email }] share credit and reminders; the owner stays primary (max 4 people)
 *   presenter             display name of the lecturer when not the owner (optional)
 *   lastReviewed          Timestamp         defaults to the upload date
 *   reviewIntervalMonths  number            default 24
 *   reviewDue             Timestamp         stored, so the reminder query is one indexed read
 *   status                'active' | 'retired'   retired items vanish from readers, stay for admins
 */

export const DEFAULT_INTERVAL_MONTHS = 24;
export const INTERVAL_OPTIONS = [6, 12, 24, 36];
export const DUE_SOON_DAYS = 30;
export const ESCALATE_AFTER_DAYS = 90;

export const toDate = (v) => {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const addMonths = (date, months) => {
  const d = new Date(date.getTime());
  d.setMonth(d.getMonth() + months);
  return d;
};

export const computeReviewDue = (lastReviewed, intervalMonths = DEFAULT_INTERVAL_MONTHS) => {
  const base = toDate(lastReviewed);
  return base ? addMonths(base, Number(intervalMonths) || DEFAULT_INTERVAL_MONTHS) : null;
};

/** Where an item stands: 'retired' | 'overdue' | 'due' | 'ok' | 'none', with the due date and days left */
export const reviewState = (item, now = new Date()) => {
  if (!item) return { state: 'none', due: null, days: null };
  if (item.status === 'retired') return { state: 'retired', due: null, days: null };
  const due = toDate(item.reviewDue) || computeReviewDue(item.lastReviewed || item.uploadDate, item.reviewIntervalMonths);
  if (!due) return { state: 'none', due: null, days: null };
  const days = Math.round((due.getTime() - now.getTime()) / 86400000);
  if (days < 0) return { state: 'overdue', due, days };
  if (days <= DUE_SOON_DAYS) return { state: 'due', due, days };
  return { state: 'ok', due, days };
};

/** Fields an item created before the lifecycle existed should get */
export const lifecycleDefaults = (item) => {
  const lastReviewed = toDate(item.lastReviewed) || toDate(item.uploadDate) || new Date();
  const interval = item.reviewIntervalMonths || DEFAULT_INTERVAL_MONTHS;
  return {
    owner: item.owner && item.owner.email ? item.owner : { name: '', email: item.uploadedBy && item.uploadedBy.includes('@') ? item.uploadedBy : '' },
    presenter: item.presenter || '',
    lastReviewed,
    reviewIntervalMonths: interval,
    reviewDue: addMonths(lastReviewed, interval),
    status: item.status || 'active',
  };
};

export const needsBackfill = (item) => !item.reviewDue || !item.status;

export const fmtMonthYear = (date) => {
  const d = toDate(date);
  return d ? d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '';
};

export const fmtDateInput = (date) => {
  const d = toDate(date);
  return d ? d.toISOString().slice(0, 10) : '';
};

/** One line for a badge: "Due in 12 days", "Overdue 40 days", "Retired" */
export const reviewLabel = (item, now = new Date()) => {
  const { state, days } = reviewState(item, now);
  if (state === 'retired') return 'Retired';
  if (state === 'overdue') return `Overdue ${Math.abs(days)} ${Math.abs(days) === 1 ? 'day' : 'days'}`;
  if (state === 'due') return days === 0 ? 'Due today' : `Due in ${days} ${days === 1 ? 'day' : 'days'}`;
  return '';
};
